import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getPlanningCredits } from '../utils/billing';
import { getConceptLabelForIntent } from '../utils/themePlanKeys';

const getSteps = (focusSelection, isThemeIdeas) => {
  const baseStep = { key: 'input', label: 'Details' };

  if (isThemeIdeas) {
    return [
      baseStep,
      { key: 'themes', label: 'Themes' }
    ];
  }

  return [
    baseStep,
    { key: 'themes', label: getConceptLabelForIntent(focusSelection, 'concepts') || 'Themes' },
    { key: 'plan', label: 'Plan' }
  ];
};

const screenToStepIndex = {
  input: 0,
  loading: 1,
  carousel: 1,
  loadingDetails: 1,
  overview: 2,
  list: 2,
  plan: 2
};

export default function TopStepperNav({
  currentScreen,
  maxStepIndex,
  completedSteps = {},
  onStepClick,
  onMenuToggle = null,
  onHomeClick = null,
  isAuthenticated = false,
  showHeader = true,
  planningMode = 'full',

  onSavePlan = null,
  onBillingClick = null,
  saveEnabled = false,
  isCollapsed = false,
  eventData = null,
  conceptsExist = false,
  planExists = false,
  onMyPlans = null
}) {
  const { user } = useAuth();
  const focusSelection = eventData?.focus_selection || [];
  const isThemeIdeas = focusSelection.length === 1 && focusSelection[0] === 'theme_ideas';
  const steps = getSteps(focusSelection, isThemeIdeas);
  const activeIndex = screenToStepIndex[currentScreen] ?? 0;
  const planningCredits = getPlanningCredits(user);

  // Determine if we should show the header/stepper
  const shouldShowHeader = showHeader && (currentScreen === 'input' || currentScreen === 'loading' || currentScreen === 'loadingDetails' || currentScreen === 'carousel' || currentScreen === 'overview' || currentScreen === 'list' || currentScreen === 'plan');

  if (!shouldShowHeader) return null;

  return (
    <>
      <header className="top-stepper-nav sticky top-0 z-[40] w-full pointer-events-none flex flex-col relative pb-2">

        {/* Row 1: Logo & Controls */}
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-6 flex justify-between items-center pointer-events-auto min-h-[44px] md:min-h-[60px] pt-1.5 md:pt-2">

          {/* Left: Mobile Menu & Logo */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {/* Mobile Menu Button - LEFT SIDE */}
            {onMenuToggle && (
              <button
                onClick={onMenuToggle}
                className="p-1.5 sm:p-2 bg-white/80 backdrop-blur-md rounded-full border border-white/20 shadow-sm hover:shadow-md text-gray-700 hover:text-black transition-all md:hidden shrink-0"
                aria-label="Open Menu"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}

            {/* Logo - Hide on desktop only if sidebar is expanded */}
            <div className={!isCollapsed ? "md:hidden" : ""}>
              {onHomeClick ? (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onHomeClick();
                  }}
                  className="group flex items-center transition-opacity hover:opacity-80 shrink-0"
                >
                  <img
                    src="/new_icon+text_color.png"
                    alt="CraftMyParty"
                    className="h-7 sm:h-9 md:h-12 w-auto object-contain"
                  />
                </button>
              ) : (
                <div className="flex items-center shrink-0">
                  <img
                    src="/new_icon+text_color.png"
                    alt="CraftMyParty"
                    className="h-7 sm:h-9 md:h-12 w-auto object-contain"
                  />
                </div>
              )}
            </div>

            {/* Demo Mode Label (Visible on desktop left, or mobile next to logo) */}
            {!isAuthenticated && (
              <span className="ds-badge ds-badge-violet px-2 py-0.5 text-[9px] sm:text-[10px] md:text-xs font-bold tracking-wide uppercase whitespace-nowrap shrink-0">
                DEMO MODE
              </span>
            )}
          </div>

          {/* Center: The Modern Floating Stepper (Now on all screens) */}
          <div className="hidden md:block absolute left-1/2 top-4 -translate-x-1/2 w-auto pointer-events-auto md:mt-12">
          <nav className="flex items-center backdrop-blur-xl border shadow-sm rounded-full p-1.5 gap-1 transition-all duration-300"
              style={{ background: 'rgba(253,251,247,0.92)', borderColor: 'var(--brand-border)' }}>
              {steps.map((step, idx) => {
                const isActive = idx === activeIndex;
                const isCompleted = (step.key === 'input' && (conceptsExist || planExists)) ||
                                    (step.key === 'themes' && planExists) ||
                                    (step.key === 'plan' && planExists);

                const isClickable = !isActive && (
                  step.key === 'input' ||
                  (step.key === 'themes' && conceptsExist) ||
                  (step.key === 'plan' && planExists)
                );

                return (
                  <button
                    key={step.key}
                    onClick={() => (isClickable ? onStepClick(step.key) : null)}
                    disabled={!isClickable && !isActive}
                    className={`
                      relative px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2
                      ${isActive
                        ? 'text-white shadow-md scale-105'
                        : isClickable
                          ? 'hover:bg-black/5 cursor-pointer opacity-100'
                          : 'cursor-not-allowed opacity-40'
                      }
                    `}
                    style={isActive ? { background: 'var(--brand-violet)' } : { color: (isCompleted || isClickable) ? 'var(--brand-charcoal)' : undefined }}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${isActive ? 'bg-white' : isCompleted ? 'bg-green-500' : 'bg-gray-300'}`} />
                    {step.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Right: Actions & User Profile */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-[40px] justify-end pointer-events-auto">
            {isAuthenticated && (
              planningCredits === 0 ? (
                <button
                  type="button"
                  onClick={onBillingClick}
                  className="inline-flex h-9 items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 sm:px-3 text-xs sm:text-sm font-bold text-amber-700 shadow-sm transition hover:border-amber-300 hover:bg-amber-100"
                  title="Add credits"
                  aria-label="Add Credits"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="hidden sm:inline">Add Credits</span>
                  <span className="sm:hidden">Add</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onBillingClick}
                  className="inline-flex h-9 items-center gap-1.5 rounded-full border border-zinc-200 bg-white/85 px-2.5 sm:px-3 text-xs sm:text-sm font-bold text-zinc-800 shadow-sm backdrop-blur-md transition hover:border-zinc-300 hover:bg-zinc-50"
                  title="View billing and planning credits"
                  aria-label={`${planningCredits} planning credits`}
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M11.983 1.907a.75.75 0 01.49.91L10.87 8.25H15a.75.75 0 01.59 1.213l-7 8.75a.75.75 0 01-1.31-.67L8.88 11.75H5a.75.75 0 01-.604-1.195l6.75-8.25a.75.75 0 01.837-.398z" />
                  </svg>
                  <span>{planningCredits}</span>
                  <span className="hidden lg:inline">Credit{planningCredits === 1 ? '' : 's'}</span>
                </button>
              )
            )}
            {/* Save Button */}
            {onSavePlan && saveEnabled && (
              <button
                onClick={onSavePlan}
                className="ds-btn-primary px-3 sm:px-4 py-1 sm:py-1.5 text-xs sm:text-sm shrink-0"
                title={isAuthenticated ? "Save Plan" : "Sign Up to Save"}
              >
                Save
              </button>
            )}
            {onMyPlans && (
              <button
                type="button"
                onClick={onMyPlans}
                className="inline-flex h-7 sm:h-9 items-center justify-center rounded-full border border-gray-200 bg-white/90 px-2.5 sm:px-3 text-xs sm:text-sm font-bold text-gray-600 shadow-sm transition-all hover:bg-gray-50 hover:text-gray-900 active:scale-95 shrink-0"
                title="Exit planning wizard"
              >
                Exit
              </button>
            )}


          </div>
        </div>

        {/* Row 2: Navigable Mobile Stepper (Previously Static Badge) */}
        <div className="w-full flex justify-center mt-2.5 md:hidden pointer-events-auto">
          <nav className="flex items-center backdrop-blur-xl border shadow-sm rounded-full p-1 gap-1"
              style={{ background: 'rgba(253,251,247,0.92)', borderColor: 'var(--brand-border)' }}>
            {steps.map((step, idx) => {
              const isActive = idx === activeIndex;
              const isCompleted = (step.key === 'input' && (conceptsExist || planExists)) ||
                                  (step.key === 'themes' && planExists) ||
                                  (step.key === 'plan' && planExists);

              const isClickable = !isActive && (
                step.key === 'input' ||
                (step.key === 'themes' && conceptsExist) ||
                (step.key === 'plan' && planExists)
              );

              return (
                <button
                  key={step.key}
                  onClick={() => (isClickable ? onStepClick(step.key) : null)}
                  disabled={!isClickable && !isActive}
                  className={`
                    relative px-3 py-1.5 rounded-full text-[11px] font-bold transition-all duration-300 flex items-center gap-1.5
                    ${isActive
                      ? 'text-white shadow-sm'
                      : isClickable
                        ? 'cursor-pointer opacity-100'
                        : 'cursor-not-allowed opacity-40'
                    }
                  `}
                  style={isActive ? { background: 'var(--brand-violet)' } : { color: (isCompleted || isClickable) ? 'var(--brand-charcoal)' : undefined }}
                >
                  <span className={`w-1 h-1 rounded-full ${isActive ? 'bg-white' : isCompleted ? 'bg-green-500' : 'bg-gray-300'}`} />
                  {step.label}
                </button>
              );
            })}
          </nav>
        </div>

      </header>


    </>
  );
}
